import { ImagePaths } from '../../lib/imageUrls.js';

export const cissp = {

    id: 9,
    slug: 'cissp',
    ogImage: '/og/courses/cissp.jpg',
    courseCode: 'CISSP-001',
    title: 'Certified Information Systems Security Professional (CISSP)',
    subtitle: 'Comprehensive CISSP certification preparation and training',
    valueProposition: 'Master the eight CISSP domains and prepare comprehensively for the industry-recognised CISSP certification exam, building expertise in enterprise security architecture and governance.',
    duration: '40 hours',
    students: 'New',
    level: 'Intermediate',
    organization: 'ISC2',
    image: ImagePaths.COURSE_4,
    shortDescription: 'CISSP-001 — CISSP exam preparation covering the eight ISC2 domains, security architecture and risk management for experienced security professionals.',
    price: {
      aud: 1999,
      currency: 'AUD',
      earlyBird: 1799
    },
    stripeConfig: {
    priceId: 'price_1T9dnfKu1nyc322UuwkASDqE',
    productId: 'prod_U7tarCVWoHA3uZ'
  },
    
    snapshot: {
      whoItsFor: [
        'Security professionals seeking CISSP certification',
        'Enterprise security architects and managers',
        'Security engineers progressing their careers',
        'IT professionals with 5+ years security experience'
      ],
      accreditation: 'Preparation for ISC² Certified Information Systems Security Professional (CISSP) Exam'
    },
    
    logistics: {
      classSize: 'Maximum 18 participants for interactive engagement',
      requirements: [
        'Minimum 5 years cumulative paid work experience in information security (or 3 years with a bachelor\'s degree)',
        'Strong foundational security knowledge',
        'Laptop with access to CISSP study resources',
        'Commitment to extensive self-study beyond course hours'
      ]
    },
    
    overview: 'Prepare comprehensively for the CISSP certification exam with this intensive 40-hour course covering all eight CISSP domains. Led by experienced security professionals with CISSP credentials, this course combines theoretical knowledge, practical applications, and exam-focused preparation. You\'ll master security architecture, risk management, cryptography, application security, identity and access management, security operations, software development security, and law/compliance relevant to Australian organisations.',
    
    outcomes: [
      'Master the eight CISSP domains and their interconnections',
      'Understand security architecture, design, and principles at enterprise scale',
      'Apply risk management and threat assessment methodologies',
      'Implement cryptographic controls and PKI concepts',
      'Design secure software development and engineering practices',
      'Manage identity, access, and privilege across enterprises',
      'Operate security services aligned to organisational governance',
      'Demonstrate exam readiness with practice tests and case studies'
    ],
    
    audience: [
      'Security professionals pursuing CISSP certification',
      'Enterprise security architects and managers',
      'Security engineers advancing careers',
      'IT professionals with 5+ years security experience',
      'Compliance and governance specialists'
    ],
    
    prerequisites: [
      'Minimum 5 years cumulative paid information security work experience',
      'Or 3 years with relevant bachelor\'s degree',
      'Strong foundational security knowledge',
      'Commitment to intensive study and exam preparation'
    ],
    
    modules: [
      {
        title: 'Security and Risk Management',
        duration: '5 hours',
        topics: [
          'Information security governance and compliance',
          'Risk management frameworks and methodologies',
          'Business continuity and disaster recovery',
          'Incident management and response',
          'Legal, regulatory, and compliance frameworks'
        ]
      },
      {
        title: 'Asset Security',
        duration: '5 hours',
        topics: [
          'Asset management and lifecycle',
          'Data classification and handling',
          'Information and data security controls',
          'Privacy and data protection principles',
          'Australian Privacy Act and compliance'
        ]
      },
      {
        title: 'Security Architecture and Engineering',
        duration: '8 hours',
        topics: [
          'Secure system architecture and design',
          'Security models and frameworks',
          'Cryptography and cryptanalysis',
          'Physical and environmental security',
          'Enterprise security architecture'
        ]
      },
      {
        title: 'Communication and Network Security',
        duration: '5 hours',
        topics: [
          'Network security architectures and protocols',
          'Secure communications and encryption',
          'Network attacks and defences',
          'Application layer security',
          'Wireless and remote access security'
        ]
      },
      {
        title: 'Identity and Access Management',
        duration: '6 hours',
        topics: [
          'Access control models and methodologies',
          'Identity management and lifecycle',
          'Authentication mechanisms and MFA',
          'Authorization and delegation',
          'Privilege and access management'
        ]
      },
      {
        title: 'Security Operations and Software Development',
        duration: '6 hours',
        topics: [
          'Secure software development lifecycle',
          'Application security and testing',
          'Security operations and monitoring',
          'Incident investigation and forensics',
          'Security awareness and training'
        ]
      },
      {
        title: 'Exam Preparation and Practice',
        duration: '5 hours',
        topics: [
          'CISSP exam format and strategy',
          'Practice exams and question analysis',
          'Domain-specific exam tips',
          'Time management and test-taking strategies',
          'Common exam pitfalls and how to avoid them'
        ]
      }
    ],
    
    format: {
      type: 'Instructor-led online with intensive study',
      includes: [
        '40 hours of expert instruction',
        'Comprehensive CISSP study materials',
        'Multiple full-length practice exams',
        'Domain-specific deep dives and case studies',
        'Live Q&A sessions with CISSP-certified instructors',
        'Exam-focused preparation strategies',
        'Certificate of completion',
        '12 months access to all course materials and updates'
      ]
    },
    
    nextIntake: {
      date: 'Register your interest — we will confirm the next cohort',
      details: 'Five-day intensive program (8 hours per day) with follow-up study support'
    },
    
    faqs: [
      {
        question: 'Do I meet the CISSP experience requirements?',
        answer: 'CISSP requires 5 years of cumulative paid security work experience, or 3 years with a relevant bachelor\'s degree. You can verify eligibility with ISC² or discuss your background with us before enrolling.'
      },
      {
        question: 'What\'s the pass rate for the CISSP exam?',
        answer: 'The CISSP exam pass rate is typically 60-65%. This course is designed to prepare you thoroughly, with many participants passing on their first attempt after completing the course and dedicated study.'
      },
      {
        question: 'How much study time should I allocate beyond the course?',
        answer: 'Most candidates allocate 100-200 hours of study beyond the 40-hour course. This includes practice exams, domain reviews, and filling knowledge gaps. We provide study schedules and resources to structure this effectively.'
      },
      {
        question: 'Are practice exams included?',
        answer: 'Yes, the course includes multiple full-length practice exams covering the actual exam format. These help you assess your readiness and identify areas needing further study.'
      },
      {
        question: 'Is this course specific to Australian requirements?',
        answer: 'While CISSP is internationally recognised, we cover Australian compliance requirements including the Privacy Act, Essential Eight, and ASIC regulations relevant to security governance.'
      },
      {
        question: 'How long after the course should I attempt the exam?',
        answer: 'We recommend attempting the CISSP exam 1-3 months after course completion, allowing time for concentrated study and practice. Your course materials and instructor support remain available throughout this period.'
      },
      {
        question: 'What happens after I pass the CISSP exam?',
        answer: 'After passing the exam and maintaining membership with ISC², you become a CISSP professional. We provide guidance on maintaining your certification through continuing education requirements.'
      },
      {
        question: 'Can this course be delivered in-house for a group?',
        answer: 'Yes, we offer customised in-house CISSP training for organisations. This allows tailoring to your team\'s specific backgrounds and focus areas. Contact us for a private quote and scheduling options.'
      }
    ]
};
